import { useCustomerCtx, useDialogCtx } from '../../../hooks/context-hooks'
import {
  invalidateNews,
  newsEndpoints,
  useSetNewsArticleStatus,
  useSetNewsArticlePinned,
  useDeleteCustomerNews,
} from '../../../api/news'
import { useTranslation } from 'react-i18next'
import { Box, Card, Chip, MenuItem, TextField, useTheme, Tooltip } from '@mui/material'
import { AddCircleOutline } from '@mui/icons-material'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import GenericTable from 'src/components/table/GenericTable'
import React, { useState, useMemo, useCallback } from 'react'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { useGetLocalityGroupsMinimalData } from 'src/api/locality-groups'
import { useGetCustomersWithAdminUserPermission } from 'src/api/customer'
import { useGetLocalitiesMinimalData } from 'src/api/localities'
import { useGetServiceStationMinimalData } from 'src/api/service-station'
import { useGetStoragesMinimalData } from 'src/api/storage'
import { GridActionButtonProps } from 'src/components/common/GridActionButton'
import { NewsType } from 'src/enums'
import { newsQueryKeys } from 'src/query-keys'
import CreateNewsArticleDialog from './CreateNewsArticleDialog'
import UpdateNewsArticleDialog from './UpdateNewsArticleDialog'
import NewsFacilityFilter from './NewsFacilityFilter'
import SelectedFilters from './SelectedFilters'
import { GridActionsCellItem, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { MdDelete, MdEdit, MdToggleOff, MdToggleOn, MdPushPin, MdOutlinePushPin, MdLock } from 'react-icons/md'
import { toast } from 'react-toastify'
import { getNewsTypeLabels } from 'src/components/news/NewsFeed'
import { IFacilityFilters } from './NewsFacilityFilter.action'

const NewsScreen = () => {
  const { customer } = useCustomerCtx()
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { t: translate } = useTranslation(['common', 'news'])
  const newsTypeLabels = getNewsTypeLabels(translate)
  const theme = useTheme()

  const [newsTypeFilter, setNewsTypeFilter] = useState<NewsType | ''>('')
  const [localityGroupFilter, setLocalityGroupFilter] = useState<number | ''>('')
  const [searchText, setSearchText] = useState('')
  const [facilityFilters, setFacilityFilters] = useState<IFacilityFilters | undefined>()

  const customerId = customer?.id as number

  const { data: customersWithPermissions } = useGetCustomersWithAdminUserPermission()
  const currentCustomer = customersWithPermissions?.find(c => c.id === customerId)

  const { mutateAsync: deleteNews, isLoading: isDeleting } = useDeleteCustomerNews(customerId)
  const { mutateAsync: setNewsStatus, isLoading: isTogglingActive } = useSetNewsArticleStatus(customerId)
  const { mutateAsync: setPinnedStatus, isLoading: isTogglingPinned } = useSetNewsArticlePinned(customerId)

  const { data: localityGroups, isLoading: isLoadingLocalityGroups } = useGetLocalityGroupsMinimalData(customerId)
  const { data: localitiesData, isLoading: isLoadingLocalities } = useGetLocalitiesMinimalData(customerId)
  const { data: serviceStationsData, isLoading: isLoadingServiceStations } = useGetServiceStationMinimalData(customerId)
  const { data: storagesData, isLoading: isLoadingStorages } = useGetStoragesMinimalData(customerId)

  const queryParams = useMemo(
    () => ({
      ...(newsTypeFilter !== '' && { newsType: newsTypeFilter }),
      ...(localityGroupFilter !== '' && { localityGroupId: localityGroupFilter }),
      ...(searchText && { search: searchText }),
      ...(facilityFilters?.localityIds?.length && { localityIds: facilityFilters.localityIds }),
      ...(facilityFilters?.serviceStationIds?.length && { serviceStationIds: facilityFilters.serviceStationIds }),
      ...(facilityFilters?.storageIds?.length && { storageIds: facilityFilters.storageIds }),
      ...(facilityFilters?.localityGroupIds?.length && { localityGroupIds: facilityFilters.localityGroupIds }),
    }),
    [newsTypeFilter, localityGroupFilter, searchText, facilityFilters],
  )

  const generateQueryKey = useCallback(
    (page: number, pageSize: number) => newsQueryKeys.GET_CUSTOMER_NEWS(customerId, { ...queryParams, page, pageSize }),
    [customerId, queryParams],
  )

  const handleEditClick = useCallback(
    async (articleId: number) => {
      await showDialog(
        UpdateNewsArticleDialog,
        {
          componentProps: {
            customerId,
            articleId,
            hasLocalities: currentCustomer?.hasLocalities,
            hasServiceStations: currentCustomer?.hasServiceStations,
            hasStorages: currentCustomer?.hasStorages,
          },
          opts: { maxWidth: 'md', fullWidth: true },
        },
        undefined,
        true,
      )
    },
    [customerId, currentCustomer, showDialog],
  )

  const handleDeleteClick = useCallback(
    (articleId: number) => {
      showConfirmDialog(
        translate('delete-news-article', { ns: 'news' }),
        translate('delete-news-article-confirm', { ns: 'news' }),
        {
          acceptText: translate('yes', { ns: 'common' }),
          cancelText: translate('no', { ns: 'common' }),
        },
        async pop => {
          await deleteNews(articleId, {
            onSuccess: () => {
              invalidateNews.all(customerId)
              toast.success(translate('Deleted', { ns: 'common' }))
            },
            onError: () => {
              toast.error(translate('deletingError', { ns: 'common' }))
            },
          })
          pop()
        },
      )
    },
    [customerId, deleteNews, showConfirmDialog, translate],
  )

  const handleToggleActive = useCallback(
    async (articleId: number, active: boolean) => {
      await setNewsStatus(
        { id: articleId, isActive: active },
        {
          onSuccess: () => {
            invalidateNews.all(customerId)
            toast.success(
              active
                ? translate('activated', { ns: 'news', defaultValue: 'News article activated' })
                : translate('deactivated', { ns: 'news', defaultValue: 'News article deactivated' }),
            )
          },
          onError: () => {
            toast.error(translate('updatingError', { ns: 'common' }))
          },
        },
      )
    },
    [customerId, setNewsStatus, translate],
  )

  const handleTogglePinned = useCallback(
    async (articleId: number, pinned: boolean) => {
      await setPinnedStatus(
        { id: articleId, isPinned: pinned },
        {
          onSuccess: () => {
            invalidateNews.all(customerId)
            toast.success(
              pinned
                ? translate('pinned', { ns: 'news', defaultValue: 'News article pinned' })
                : translate('unpinned', { ns: 'news', defaultValue: 'News article unpinned' }),
            )
          },
          onError: () => {
            toast.error(translate('updatingError', { ns: 'common' }))
          },
        },
      )
    },
    [customerId, setPinnedStatus, translate],
  )

  const columns = useMemo(
    () => [
      {
        field: 'isPinned',
        headerName: '',
        width: 40,
        sortable: false,
        renderCell: (params: any) =>
          params.row.isPinned ? (
            <MdPushPin
              size={18}
              color={getThemeColor(theme, MUIThemeColors.primaryMain)}
              title={translate('pinned', { ns: 'news', defaultValue: 'Pinned' })}
            />
          ) : (
            <></>
          ),
      },
      {
        field: 'title',
        headerName: translate('title', { ns: 'common' }),
        flex: 3,
        minWidth: 200,
      },
      {
        field: 'newsType',
        headerName: translate('type', { ns: 'common' }),
        flex: 1.5,
        minWidth: 120,
        renderCell: (params: any) => <Chip label={newsTypeLabels[params.row.newsType as NewsType]} size="small" />,
      },
      {
        field: 'createdAt',
        headerName: translate('createdAt', { ns: 'common' }),
        flex: 2,
        minWidth: 150,
        renderCell: (params: any) => getFormattedDate(params.row.createdAt),
      },
      {
        field: 'createdByUserFirstName',
        headerName: translate('created-by', { ns: 'common' }),
        flex: 2,
        minWidth: 150,
        renderCell: (params: any) => {
          const parts = [params.row.createdByUserFirstName, params.row.createdByUserLastName].filter(Boolean)
          return parts.join(' ') || '-'
        },
      },
      {
        field: 'isActive',
        headerName: translate('status', { ns: 'common' }),
        flex: 1.5,
        minWidth: 100,
        renderCell: (params: any) =>
          params.row.isActive ? (
            <Chip label={translate('active', { ns: 'common', defaultValue: 'Active' })} size="small" color="success" />
          ) : (
            <Chip
              label={translate('inactive', { ns: 'common', defaultValue: 'Inactive' })}
              size="small"
              color="default"
            />
          ),
      },
      {
        field: 'expiresAt',
        headerName: translate('expires-at', { ns: 'common' }),
        flex: 2,
        minWidth: 150,
        renderCell: (params: any) => getFormattedDate(params.row.expiresAt),
      },
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        headerName: '',
        width: 60,
        getActions: ({ id, row }: any) => {
          if (row.customerId == null) {
            return [
              <Tooltip
                key={`locked-${id}`}
                title={`${translate('article-added-by-super-admin', { ns: 'news' })}`}
                arrow
              >
                <GridActionsCellItem
                  label={translate('article-added-by-super-admin', { ns: 'news' })}
                  icon={<MdLock size={20} />}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  showInMenu={false}
                  color="primary"
                />
              </Tooltip>,
            ]
          }
          return [
            <GridActionsCellItem
              key={`edit-${id}`}
              label={translate('edit', { ns: 'common' })}
              icon={<MdEdit size={24} />}
              onClick={() => handleEditClick(row.id)}
              disabled={isDeleting || isTogglingActive}
              color="primary"
              showInMenu
            />,
            <GridActionsCellItem
              key={`activate-${id}`}
              label={translate(row.isActive ? 'deactivate' : 'activate', { ns: 'common' })}
              icon={row.isActive ? <MdToggleOff size={24} /> : <MdToggleOn size={24} />}
              onClick={() => handleToggleActive(row.id, !row.isActive)}
              disabled={isDeleting || isTogglingActive || isTogglingPinned}
              color="primary"
              showInMenu
            />,
            <GridActionsCellItem
              key={`pin-${id}`}
              label={translate(row.isPinned ? 'unpin' : 'pin', {
                ns: 'news',
                defaultValue: row.isPinned ? 'Unpin' : 'Pin',
              })}
              icon={row.isPinned ? <MdPushPin size={24} /> : <MdOutlinePushPin size={24} />}
              onClick={() => handleTogglePinned(row.id, !row.isPinned)}
              disabled={isDeleting || isTogglingActive || isTogglingPinned}
              color="primary"
              showInMenu
            />,
            <GridActionsCellItem
              key={`delete-${id}`}
              label={translate('delete', { ns: 'common' })}
              icon={<MdDelete size={24} />}
              onClick={() => handleDeleteClick(row.id)}
              disabled={isDeleting || isTogglingActive || isTogglingPinned}
              color="primary"
              showInMenu
            />,
          ]
        },
      },
    ],
    [
      translate,
      isDeleting,
      isTogglingActive,
      isTogglingPinned,
      handleEditClick,
      handleDeleteClick,
      handleToggleActive,
      handleTogglePinned,
      theme,
    ],
  )

  const handleCreateClick = useCallback(async () => {
    await showDialog(
      CreateNewsArticleDialog,
      {
        componentProps: {
          customerId,
          hasLocalities: currentCustomer?.hasLocalities,
          hasServiceStations: currentCustomer?.hasServiceStations,
          hasStorages: currentCustomer?.hasStorages,
        },
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }, [customerId, showDialog, currentCustomer])

  const topAction: GridActionButtonProps[] = [
    {
      buttonText: translate('create', { ns: 'common' }),
      variant: 'outlined',
      shadowWeight: null,
      buttonIcon: <AddCircleOutline />,
      onClick: handleCreateClick,
    },
  ]

  return (
    <CommonPageLayout titleSection={{ title: translate('news', { ns: 'common' }) }} topAction={topAction}>
      <Card sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box flex={2} minWidth={250}>
            <TextField
              fullWidth
              size="small"
              label={translate('search', { ns: 'common' })}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </Box>

          <Box flex={1} minWidth={180}>
            <TextField
              select
              fullWidth
              size="small"
              label={translate('type', { ns: 'common' })}
              value={newsTypeFilter}
              onChange={e => setNewsTypeFilter(e.target.value === '' ? '' : (parseInt(e.target.value, 10) as NewsType))}
            >
              <MenuItem value="">{translate('all', { ns: 'common' })}</MenuItem>
              {Object.entries(newsTypeLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box flex={1} minWidth={180}>
            <NewsFacilityFilter
              customerId={customerId}
              facilityFilters={facilityFilters}
              onFiltersChange={setFacilityFilters}
              localitiesData={localitiesData}
              serviceStationsData={serviceStationsData}
              storagesData={storagesData}
              localityGroupsData={localityGroups}
              isLoadingLocalities={isLoadingLocalities}
              isLoadingServiceStations={isLoadingServiceStations}
              isLoadingStorages={isLoadingStorages}
              isLoadingLocalityGroups={isLoadingLocalityGroups}
            />
          </Box>
        </Box>
        <SelectedFilters
          facilityFilters={facilityFilters}
          localitiesData={localitiesData}
          serviceStationsData={serviceStationsData}
          storagesData={storagesData}
          localityGroups={localityGroups}
          onFilterChange={setFacilityFilters}
        />
      </Card>

      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <GenericTable
          id="admin-news-table"
          columns={columns}
          enableServerPagination
          enablePagination
          pageSize={20}
          pageOptions={[10, 20, 50]}
          queryUrl={newsEndpoints.getCustomerNews(customerId)}
          queryParams={queryParams}
          generateQueryKey={generateQueryKey}
          noRowsMessage={translate('no-news-found', { ns: 'common' })}
          autoHeight={true}
          minRowHeight="36px"
          getRowHeight={() => 40}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              minHeight: '32px !important',
              maxHeight: '32px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              py: 0.5,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '12px',
              fontWeight: 500,
            },
          }}
        />
      </Box>
    </CommonPageLayout>
  )
}

export default NewsScreen
